import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiTransfersIndex } from "../lib/api";

type Recipient = { id: number; name: string };
type Transfer = {
  id: number;
  recipient?: Recipient | null;
  transfer_type: "sepa" | "swift" | string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
};

type Paginator<T> = {
  data: T[];
  current_page: number;
  last_page: number;
  links?: { url: string | null; label: string; active: boolean }[];
};

export default function TransfersIndex() {
  const [sp, setSp] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [pager, setPager] = useState<Paginator<Transfer> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modules, setModules] = useState<Record<string, { enabled?: boolean }>>({});
  const [trust, setTrust] = useState<{ id?: number | string; status?: string } | null>(null);

  const search = sp.get("search") ?? "";
  const status = sp.get("status") ?? "";
  const transfers_page = sp.get("transfers_page") ?? "1";

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiTransfersIndex({
        search: search || undefined,
        status: status || undefined,
        transfers_page,
      });
      setPager(res);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load transfers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, transfers_page]);

  useEffect(() => {
    let alive = true;
    apiGet<any>("/xapi/me")
      .then((res) => {
        if (!alive) return;
        setModules(res?.modules ?? {});
        setTrust(res?.trust ?? null);
      })
      .catch(() => {
        if (alive) setModules({});
      });

    return () => {
      alive = false;
    };
  }, []);

  const onSubmitFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const next = new URLSearchParams(sp);
    next.set("search", String(fd.get("search") ?? ""));
    next.set("status", String(fd.get("status") ?? ""));
    next.set("transfers_page", "1");
    setSp(next, { replace: false });
  };

  const goPage = (page: number) => {
    const next = new URLSearchParams(sp);
    next.set("transfers_page", String(page));
    setSp(next, { replace: false });
  };

  const trustEnabled = modules.trust?.enabled ?? true;
  const fxEnabled = modules.fx?.enabled ?? true;
  const trustActive = ["active", "approved"].includes(String(trust?.status ?? "").toLowerCase());

  const actions = useMemo(
    () => (
      <div className="flex flex-wrap gap-2">
        {trustEnabled && trustActive && (
          <Link to="/trust/transfer" className="btn btn-white">
            Transfer to Trust
          </Link>
        )}
        <Link to="/transfers/create" className="btn btn-white">
          Create Transfer Instruction
        </Link>
        {fxEnabled && (
          <Link to="/fx/create" className="btn btn-white">
            Currency Conversion Request
          </Link>
        )}
      </div>
    ),
    [fxEnabled, trustActive, trustEnabled]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 flex-wrap">
        {actions}
        <form onSubmit={onSubmitFilters} className="flex gap-2 ml-auto">
          <input
            name="search"
            defaultValue={search}
            placeholder="Search reference or recipient"
            className="px-3 py-2 border rounded"
          />
          <select name="status" defaultValue={status} className="px-3 py-2 border rounded">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="executed">Executed</option>
            <option value="failed">Failed</option>
            <option value="rejected">Rejected</option>
          </select>
          <button className="btn">Filter</button>
        </form>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
        <table className="min-w-full table-auto text-sm text-left">
          <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Recipient</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Amount</th>
              <th className="px-4 py-2">Currency</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6">
                  <div className="skeleton h-6 w-1/2" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-red-600">
                  {error}
                </td>
              </tr>
            ) : pager && pager.data.length ? (
              pager.data.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">{new Date(t.created_at).toISOString().slice(0, 10)}</td>
                  <td className="px-4 py-2">{t.recipient?.name ?? "—"}</td>
                  <td className="px-4 py-2 uppercase">{t.transfer_type}</td>
                  <td className="px-4 py-2">{Number(t.amount).toFixed(2)}</td>
                  <td className="px-4 py-2">{t.currency}</td>
                  <td className="px-4 py-2 capitalize">{t.status}</td>
                  <td className="px-4 py-2">
                    <Link to={`/transfers/${t.id}`} className="text-blue-600 hover:underline">
                      View
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-gray-500 dark:text-gray-400">
                  No transfers yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Laravel-style pagination */}
        {pager && pager.last_page > 1 && (
          <div className="mt-4 px-4 pb-4 flex gap-2">
            <button
              className="btn btn-white"
              disabled={pager.current_page <= 1}
              onClick={() => goPage(pager.current_page - 1)}
            >
              Prev
            </button>
            <span className="px-2 py-2">
              Page {pager.current_page} of {pager.last_page}
            </span>
            <button
              className="btn btn-white"
              disabled={pager.current_page >= pager.last_page}
              onClick={() => goPage(pager.current_page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-4">
        {loading ? (
          <div className="skeleton h-24 w-full rounded" />
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : pager && pager.data.length ? (
          pager.data.map((t) => (
            <div key={t.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900 shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                  {t.recipient?.name ?? "—"}
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(t.created_at).toISOString().slice(0, 10)}
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                {String(t.transfer_type).toUpperCase()} • {Number(t.amount).toFixed(2)} {t.currency}
              </p>
              <p className="text-sm mb-2">
                <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>{" "}
                <span className="text-gray-800 dark:text-gray-100">{t.status}</span>
              </p>
              <Link to={`/transfers/${t.id}`} className="inline-block text-sm text-blue-600 hover:underline">
                View
              </Link>
            </div>
          ))
        ) : (
          <p className="text-center text-sm text-gray-500">No transfers yet.</p>
        )}
      </div>
    </div>
  );
}
